import React, { useContext, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CRMContext } from '../context/CRMContext';
import { useUsers } from '../context/UsersContext';
import SafeIcon from '../common/SafeIcon';
import { FiPhone, FiMail, FiMessageCircle, FiSend, FiEdit2, FiArrowLeft, FiPlus, FiCheckCircle, FiClock, FiCalendar, FiExternalLink, FiTag, FiUserCheck, FiUserPlus, FiUser } from 'react-icons/fi';
import { format } from 'date-fns';
import { LeadModal, EventModal } from '../components/Modals';

const statusStyles = {
  New:       { backgroundColor: '#EFF6FF', color: '#2563EB', label: 'Nuevo' },
  Contacted: { backgroundColor: '#F0F9FF', color: '#0284C7', label: 'Contactado' },
  Qualified: { backgroundColor: '#F0FDF4', color: '#16A34A', label: 'Calificado' },
  Won:       { backgroundColor: '#FFFBEB', color: '#D97706', label: 'Ganado' },
  Lost:      { backgroundColor: '#F1F5F9', color: '#64748B', label: 'Perdido' },
};

const LeadProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leads, addLead, updateLead, events, addEvent, updateEvent, notes, addNote, activities } = useContext(CRMContext);
  const { getUserById } = useUsers();

  const [activeTab, setActiveTab] = useState('events');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isAddReferralModalOpen, setIsAddReferralModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [newNote, setNewNote] = useState('');

  const lead = leads.find(l => l.id === id);

  if (!lead) return (
    <div className="p-8 text-center">
      Lead no encontrado.{' '}
      <button onClick={() => navigate('/leads')} style={{ color: '#2563EB' }} className="underline">Volver</button>
    </div>
  );

  const leadEvents = events.filter(e => e.leadId === id).sort((a, b) => new Date(b.date) - new Date(a.date));
  const leadNotes = notes.filter(n => n.leadId === id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const leadActivities = activities.filter(a => a.leadId === id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const referredLeads = leads.filter(l => l.referrerLeadId === id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const referrerLead = lead.referrerLeadId ? leads.find(l => l.id === lead.referrerLeadId) : null;

  const handleQuickAction = (type, value) => {
    if (!value) return;
    if (type === 'call') window.open(`tel:${value}`);
    if (type === 'whatsapp') window.open(`https://wa.me/${value.replace(/\D/g, '')}`);
    if (type === 'email') window.open(`mailto:${value}`);
    if (type === 'telegram') window.open(`https://t.me/${value.replace('@', '')}`);
  };

  const submitNote = (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    addNote(id, newNote);
    setNewNote('');
  };

  const resolveActorName = (entry) => {
    if (entry.actorName) return entry.actorName;
    if (entry.actorId) {
      const u = getUserById(entry.actorId);
      return u ? u.name : 'Desconocido';
    }
    return null;
  };

  const tabs = ['events', 'notes', 'history', 'referrals'];
  const tabLabels = {
    events: 'Tareas y Citas',
    notes: 'Notas',
    history: 'Historial',
    referrals: `Referidos (${referredLeads.length})`
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <button onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm mb-5 font-medium transition-colors" style={{ color: '#64748B' }} onMouseEnter={e => e.currentTarget.style.color = '#0F172A'} onMouseLeave={e => e.currentTarget.style.color = '#64748B'}>
          <SafeIcon icon={FiArrowLeft} /> Volver a Leads
        </button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>{lead.name}</h1>
              <span className="px-2.5 py-1 text-xs font-bold rounded-full uppercase tracking-wide" style={statusStyles[lead.status] || statusStyles.Lost}>
                {(statusStyles[lead.status] || statusStyles.Lost).label}
              </span>
            </div>
            <p className="font-semibold mb-6 text-sm" style={{ color: '#64748B' }}>{lead.company || 'Persona Particular'}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Teléfonos</h4>
                  <div className="space-y-2">
                    {lead.phones?.length ? lead.phones.map((p, i) => (
                      <div key={i} className="flex items-center justify-between group">
                        <div className="text-sm">
                          <span className="capitalize w-16 inline-block" style={{ color: '#94A3B8' }}>{p.label === 'main' ? 'Principal' : p.label}:</span>
                          <span className="font-medium" style={{ color: '#0F172A' }}>{p.value}</span>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleQuickAction('call', p.value)} className="p-1 rounded" style={{ color: '#2563EB' }}><SafeIcon icon={FiPhone} className="text-xs" /></button>
                          <button onClick={() => handleQuickAction('whatsapp', p.value)} className="p-1 rounded" style={{ color: '#14B8A6' }}><SafeIcon icon={FiMessageCircle} className="text-xs" /></button>
                        </div>
                      </div>
                    )) : <p className="text-sm italic" style={{ color: '#CBD5E1' }}>Sin números registrados</p>}
                  </div>
                </div>

                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Correos</h4>
                  <div className="space-y-2">
                    {lead.emails?.length ? lead.emails.map((e, i) => (
                      <div key={i} className="flex items-center justify-between group">
                        <div className="text-sm">
                          <span className="capitalize w-16 inline-block" style={{ color: '#94A3B8' }}>{e.label === 'main' ? 'Principal' : e.label}:</span>
                          <span className="font-medium" style={{ color: '#0F172A' }}>{e.value}</span>
                        </div>
                        <button onClick={() => handleQuickAction('email', e.value)} className="opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity" style={{ color: '#EF4444' }}><SafeIcon icon={FiMail} className="text-xs" /></button>
                      </div>
                    )) : <p className="text-sm italic" style={{ color: '#CBD5E1' }}>Sin correos registrados</p>}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Origen del Lead</h4>
                  <p className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full font-medium" style={{ backgroundColor: '#F0F9FF', color: '#0284C7', border: '1px solid #E0F2FE' }}>
                    <SafeIcon icon={FiTag} style={{ color: '#38BDF8' }} /> {lead.source === 'Website' ? 'Sitio Web' : lead.source === 'Recommended' ? 'Recomendado' : lead.source}
                  </p>
                </div>
                {lead.source === 'Recommended' && (
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Referido por</h4>
                    {lead.referrerType === 'linked' && referrerLead ? (
                      <Link to={`/leads/${referrerLead.id}`} className="inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: '#2563EB' }}>
                        <SafeIcon icon={FiUserCheck} /> {referrerLead.name} <SafeIcon icon={FiExternalLink} className="text-[10px]" />
                      </Link>
                    ) : (
                      <p className="inline-flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
                        <SafeIcon icon={FiUserCheck} style={{ color: '#94A3B8' }} /> {lead.referrerManual || 'Anónimo'} <span className="text-[10px]" style={{ color: '#94A3B8' }}>(Manual)</span>
                      </p>
                    )}
                  </div>
                )}
                {lead.telegram && (
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Telegram</h4>
                    <button onClick={() => handleQuickAction('telegram', lead.telegram)} className="text-sm hover:underline flex items-center gap-1" style={{ color: '#38BDF8' }}>
                      <SafeIcon icon={FiSend} className="text-xs" /> @{lead.telegram.replace('@', '')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="min-w-[180px]">
            <button onClick={() => setIsEditModalOpen(true)} className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg text-white font-semibold text-sm transition-all" style={{ backgroundColor: '#2563EB', boxShadow: '0 1px 3px rgba(37,99,235,0.3)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1D4ED8'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}>
              <SafeIcon icon={FiEdit2} /> Editar Lead
            </button>
            <p className="text-[10px] mt-4 text-center uppercase tracking-wider font-bold" style={{ color: '#94A3B8' }}>
              Creado el {format(new Date(lead.createdAt), 'd MMM, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs Card */}
      <div className="bg-white rounded-xl overflow-hidden min-h-[400px]" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex" style={{ borderBottom: '1px solid #E2E8F0' }}>
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className="flex-1 py-4 text-sm font-bold text-center capitalize transition-all" style={activeTab === tab ? { borderBottom: '2px solid #2563EB', color: '#2563EB', backgroundColor: '#F0F6FF' } : { color: '#94A3B8', borderBottom: '2px solid transparent' }} onMouseEnter={e => { if (activeTab !== tab) { e.currentTarget.style.color = '#334155'; e.currentTarget.style.backgroundColor = '#F8FAFC'; } }} onMouseLeave={e => { if (activeTab !== tab) { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.backgroundColor = 'transparent'; } }}>
              {tabLabels[tab]}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: '#0F172A' }}>Tareas Pendientes</h3>
                <button onClick={() => { setEventToEdit(null); setIsEventModalOpen(true); }} className="flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: '#2563EB' }}>
                  <SafeIcon icon={FiPlus} /> Agregar Tarea
                </button>
              </div>

              {leadEvents.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#94A3B8' }}>Sin tareas o citas registradas.</p>
              ) : (
                <div className="space-y-3">
                  {leadEvents.map(evt => (
                    <div key={evt.id} className="p-4 rounded-xl flex items-start gap-4" style={evt.status === 'completed' ? { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', opacity: 0.7 } : { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                      <button onClick={() => updateEvent(evt.id, { ...evt, status: evt.status === 'completed' ? 'pending' : 'completed' })} className="mt-1 flex-shrink-0 transition-colors" style={{ color: evt.status === 'completed' ? '#14B8A6' : '#CBD5E1' }}>
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <SafeIcon icon={evt.type === 'appointment' ? FiCalendar : FiClock} className="text-xs" style={{ color: evt.type === 'appointment' ? '#14B8A6' : '#F59E0B' }} />
                          <h4 className="font-semibold truncate text-sm" style={{ color: evt.status === 'completed' ? '#94A3B8' : '#0F172A', textDecoration: evt.status === 'completed' ? 'line-through' : 'none' }}>
                            {evt.title}
                          </h4>
                        </div>
                        <p className="text-xs mb-2" style={{ color: '#94A3B8' }}>{format(new Date(evt.date), "d MMM, yyyy • h:mm a")}</p>
                        {evt.description && <p className="text-sm p-2 rounded-lg" style={{ color: '#64748B', backgroundColor: '#F8FAFC' }}>{evt.description}</p>}
                      </div>
                      <button onClick={() => { setEventToEdit(evt); setIsEventModalOpen(true); }} className="transition-colors" style={{ color: '#CBD5E1' }} onMouseEnter={e => e.currentTarget.style.color = '#2563EB'} onMouseLeave={e => e.currentTarget.style.color = '#CBD5E1'}>
                        <SafeIcon icon={FiEdit2} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="space-y-6">
              <form onSubmit={submitNote} className="flex gap-3">
                <input 
                  type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                  placeholder="Escribe una nota..." 
                  className="flex-1 px-4 py-2 rounded-lg text-sm outline-none"
                  style={{ border: '1px solid #E2E8F0', color: '#0F172A' }}
                  onFocus={e => e.target.style.borderColor = '#2563EB'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
                <button type="submit" disabled={!newNote.trim()} className="px-6 py-2 rounded-lg font-semibold text-white text-sm transition-all disabled:opacity-50" style={{ backgroundColor: '#2563EB' }} onMouseEnter={e => { if (newNote.trim()) e.currentTarget.style.backgroundColor = '#1D4ED8'; }} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}>
                  Agregar Nota
                </button>
              </form>
              <div className="space-y-3">
                {leadNotes.map(note => {
                  const authorName = resolveActorName(note);
                  return (
                    <div key={note.id} className="p-4 rounded-xl" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                      <p className="text-sm whitespace-pre-wrap" style={{ color: '#0F172A' }}>{note.content}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94A3B8' }}>
                          {format(new Date(note.createdAt), 'd MMM, yyyy · h:mm a')}
                        </p>
                        {authorName && (
                          <>
                            <span className="text-[10px]" style={{ color: '#CBD5E1' }}>·</span>
                            <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: '#94A3B8' }}>
                              <SafeIcon icon={FiUser} className="text-[9px]" /> Agregado por <span className="font-bold ml-0.5" style={{ color: '#64748B' }}>{authorName}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="relative pl-6 space-y-8 py-2" style={{ borderLeft: '2px solid #E2E8F0' }}>
              {leadActivities.map(act => {
                const actorName = resolveActorName(act);
                const actionLabels = {
                  'Lead Created': 'Lead Creado',
                  'Note Added': 'Nota Agregada',
                  'Task Created': 'Tarea Creada',
                  'Task Completed': 'Tarea Completada',
                  'Appointment Created': 'Cita Creada',
                  'Lead Edited': 'Lead Editado'
                };
                return (
                  <div key={act.id} className="relative">
                    <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-white border-2" style={{ borderColor: '#2563EB' }} />
                    <p className="text-sm font-black uppercase tracking-tight" style={{ color: '#0F172A' }}>{actionLabels[act.action] || act.action}</p>
                    <p className="text-sm mt-0.5" style={{ color: '#64748B' }}>{act.description}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94A3B8' }}>
                        {format(new Date(act.createdAt), 'd MMM, yyyy · h:mm a')}
                      </p>
                      {actorName && (
                        <>
                          <span className="text-[10px]" style={{ color: '#CBD5E1' }}>·</span>
                          <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: '#94A3B8' }}>
                            <SafeIcon icon={FiUser} className="text-[9px]" /> por <span className="font-bold ml-0.5" style={{ color: '#64748B' }}>{actorName}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: '#0F172A' }}>Leads Referidos</h3>
                <button onClick={() => setIsAddReferralModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold uppercase tracking-tight transition-all" style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#DBEAFE'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#EFF6FF'}>
                  <SafeIcon icon={FiUserPlus} /> Agregar Referido
                </button>
              </div>

              {referredLeads.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#94A3B8' }}>No hay referidos registrados para este lead aún.</p>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {referredLeads.map(ref => (
                    <div key={ref.id} className="p-4 rounded-xl flex items-center justify-between transition-colors" style={{ border: '1px solid #E2E8F0' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <div>
                        <p className="font-semibold text-sm" style={{ color: '#0F172A' }}>{ref.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{ref.company || 'Persona Particular'}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md" style={statusStyles[ref.status] || statusStyles.Lost}>
                            {(statusStyles[ref.status] || statusStyles.Lost).label}
                          </span>
                          <span className="text-[10px] font-medium flex items-center gap-1" style={{ color: '#94A3B8' }}>
                            <SafeIcon icon={FiTag} className="text-[8px]" /> {ref.source === 'Website' ? 'Sitio Web' : ref.source}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] mb-2 uppercase font-bold" style={{ color: '#94A3B8' }}>
                          Referido el {format(new Date(ref.createdAt), 'd MMM, yyyy')}
                        </p>
                        <Link to={`/leads/${ref.id}`} className="inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: '#2563EB' }}>
                          Ver Perfil <SafeIcon icon={FiExternalLink} className="text-xs" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LeadModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSubmit={data => updateLead(id, data)} initialData={lead} />
      <EventModal isOpen={isEventModalOpen} onClose={() => setIsEventModalOpen(false)} onSubmit={data => eventToEdit ? updateEvent(data.id, data) : addEvent(data)} leads={[]} fixedLeadId={id} initialData={eventToEdit} />
      <LeadModal isOpen={isAddReferralModalOpen} onClose={() => setIsAddReferralModalOpen(false)} onSubmit={addLead} initialData={{ source: 'Recommended', referrerType: 'linked', referrerLeadId: id }} />
    </div>
  );
};

export default LeadProfile;